import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { AvisoDoLote, useAcaoEmLote } from '../../componentes/AcaoEmLote';
import { Botao, CampoDeSelecao, Modal, classesDeBotao } from '../../componentes/ui';
import {
  CHAVE_DOS_QUADROS,
  carregarQuadro,
  carregarQuadros,
  chaveDoQuadro,
  criarCard,
} from '../../servicos/crm';
import { resumirNomes } from '../../servicos/lote';
import { ErroDaApi } from '../ErroDaApi';

export interface EmpresaParaVincular {
  readonly id: string;
  readonly nome: string;
}

/**
 * "Vincular CRM" de uma ou várias empresas: escolhe o quadro e a coluna e cria
 * um card para cada uma.
 *
 * Vai empresa a empresa, pela mesma rota do "Vincular ao CRM" do cadastro
 * (`POST /crm/cards`): o servidor dispara as automações da coluna e recusa a
 * empresa que já está no quadro — e o desfecho diz onde ela está, em vez de
 * duplicar o card. Abre no quadro padrão (o primeiro) e na primeira coluna.
 */
export function VincularAoCrm(props: {
  /** `null` = fechado. */
  readonly empresas: readonly EmpresaParaVincular[] | null;
  readonly aoFechar: () => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const aberto = props.empresas !== null;
  const [quadroId, setQuadroId] = useState('');
  const [colunaId, setColunaId] = useState('');

  const quadros = useQuery({
    queryKey: CHAVE_DOS_QUADROS,
    queryFn: carregarQuadros,
    enabled: aberto,
  });
  const quadro = useQuery({
    queryKey: chaveDoQuadro(quadroId),
    queryFn: () => carregarQuadro(quadroId),
    enabled: aberto && quadroId !== '',
  });

  const vincular = useAcaoEmLote<EmpresaParaVincular>({
    acao: (e) => criarCard(e.id, colunaId === '' ? { quadroId } : { colunaId }),
    nome: (e) => e.nome,
    // O pipeline, os botões "Abrir no CRM" do cadastro e a Prospecção.
    aoTerminar: () => void queryClient.invalidateQueries({ queryKey: ['crm'] }),
  });

  // Abre no quadro padrão; trocar de quadro volta para a primeira coluna dele.
  const primeiroQuadro = quadros.data?.[0]?.id;
  useEffect(() => {
    if (aberto && quadroId === '' && primeiroQuadro !== undefined) setQuadroId(primeiroQuadro);
  }, [aberto, quadroId, primeiroQuadro]);
  const colunas = [...(quadro.data?.colunas ?? [])].sort((a, b) => a.ordem - b.ordem);
  const primeiraColuna = colunas[0]?.id;
  useEffect(() => {
    if (colunaId === '' && primeiraColuna !== undefined) setColunaId(primeiraColuna);
  }, [colunaId, primeiraColuna]);

  const fechar = (): void => {
    vincular.limparDesfecho();
    setQuadroId('');
    setColunaId('');
    props.aoFechar();
  };

  const empresas = props.empresas ?? [];
  const umaSo = empresas.length === 1;
  const terminou = vincular.desfecho !== null;
  const nomeDoQuadro = quadros.data?.find((q) => q.id === quadroId)?.nome ?? '';

  return (
    <Modal
      aberto={aberto}
      aoFechar={fechar}
      titulo={
        umaSo
          ? `Vincular ${empresas[0]!.nome} ao CRM`
          : `Vincular ${empresas.length} empresas ao CRM`
      }
      descricao={umaSo ? undefined : `${resumirNomes(empresas.map((e) => e.nome))}.`}
      rodape={
        terminou ? (
          <>
            <Botao variante="secundario" onClick={fechar}>
              Fechar
            </Botao>
            <Link
              to={`/crm/pipeline?quadro=${quadroId}`}
              className={classesDeBotao('primario', 'medio')}
              onClick={fechar}
            >
              Abrir o quadro
            </Link>
          </>
        ) : (
          <>
            <Botao variante="secundario" onClick={fechar} disabled={vincular.executando}>
              Cancelar
            </Botao>
            <Botao
              carregando={vincular.executando}
              disabled={quadroId === '' || colunaId === ''}
              onClick={() => void vincular.executar(empresas)}
            >
              {umaSo ? 'Criar card' : `Criar ${empresas.length} cards`}
            </Botao>
          </>
        )
      }
    >
      {terminou ? (
        <AvisoDoLote
          desfecho={vincular.desfecho}
          feitos={(n) =>
            n === 1
              ? `1 card criado em "${nomeDoQuadro}"`
              : `${n} cards criados em "${nomeDoQuadro}"`
          }
          aoFechar={fechar}
        />
      ) : quadros.isError ? (
        <ErroDaApi titulo="Não foi possível carregar os quadros do CRM" erro={quadros.error} />
      ) : (
        <div className="flex flex-col gap-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <CampoDeSelecao
              rotulo="Quadro"
              value={quadroId}
              disabled={quadros.data === undefined}
              onChange={(e) => {
                setQuadroId(e.target.value);
                setColunaId('');
              }}
              opcoes={(quadros.data ?? []).map((q) => ({ valor: q.id, rotulo: q.nome }))}
            />
            <CampoDeSelecao
              rotulo="Coluna"
              value={colunaId}
              disabled={colunas.length === 0}
              onChange={(e) => setColunaId(e.target.value)}
              opcoes={colunas.map((c) => ({ valor: c.id, rotulo: c.nome }))}
            />
          </div>
          {quadro.isError && (
            <ErroDaApi titulo="Não foi possível carregar as colunas" erro={quadro.error} />
          )}
          <p className="text-sm text-conteudo-sutil">
            {umaSo ? 'A empresa' : 'Cada empresa'} vira um card na coluna escolhida, com as
            automações dela. {umaSo ? 'Se já estiver' : 'As que já estiverem'} neste quadro ficam
            como estão — o aviso no fim diz onde.
          </p>
        </div>
      )}
    </Modal>
  );
}
